import { useState, useEffect, useCallback } from 'react';
import { CONSENT_EVENT } from '@/lib/consent';
import { purgeCategories, type ConsentCategory } from '@/lib/cookieLibrary';

export type ConsentState = 'granted' | 'denied';

export interface ConsentPreferences {
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  analytics_storage: ConsentState;
  functionality_storage: ConsentState;
  personalization_storage: ConsentState;
  security_storage: ConsentState;
}

/** The banner's categories (see src/lib/cookieLibrary.ts for what each covers). */
export interface UserPreferences {
  necessary: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
}

const CONSENT_STORAGE_KEY = 'eyecaptain_cookie_consent';
const CONSENT_TIMESTAMP_KEY = 'eyecaptain_cookie_consent_timestamp';
const CONSENT_VERSION_KEY = 'eyecaptain_cookie_consent_version';
// 3: three categories (the empty "Personalisation" one is gone), so everyone
// answers once more. index.html restores only this version.
const CURRENT_CONSENT_VERSION = '3';
// GDPR best practice: re-ask consent every 6 months
const CONSENT_EXPIRY_MS = 1000 * 60 * 60 * 24 * 180;

// Safe localStorage helpers — some browsers (Safari ITP, strict privacy modes,
// 3rd-party iframe contexts) throw on access. Without this, the banner would
// never render because the init effect would crash silently.
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

// Validate that a parsed consent object has all required keys with valid values.
const isValidConsent = (obj: unknown): obj is ConsentPreferences => {
  if (!obj || typeof obj !== 'object') return false;
  const required: (keyof ConsentPreferences)[] = [
    'ad_storage',
    'ad_user_data',
    'ad_personalization',
    'analytics_storage',
    'functionality_storage',
    'personalization_storage',
    'security_storage',
  ];
  const o = obj as Record<string, unknown>;
  return required.every((k) => o[k] === 'granted' || o[k] === 'denied');
};

// Default denied state for GDPR compliance
const DEFAULT_CONSENT: ConsentPreferences = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted', // Security is always granted
};

// Initialize dataLayer (gtag is handled by TrackingManager)
const ensureDataLayer = () => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
};

// Update consent state - only called when user explicitly changes consent
const updateConsent = (preferences: ConsentPreferences) => {
  ensureDataLayer();

  // Push gtag consent update. Use window.gtag when available; otherwise
  // fall back to pushing an arguments-like array to dataLayer so GTM still
  // picks up the Consent Mode V2 update.
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', preferences);
  } else {
    // Mimic gtag('consent','update', prefs) which pushes an Arguments object
    window.dataLayer.push(['consent', 'update', preferences] as any);
  }
  
  // Push cookie_consent_update event - only on explicit user action
  window.dataLayer.push({
    event: 'cookie_consent_update',
    consent_state: preferences,
    analytics_storage: preferences.analytics_storage,
    ad_storage: preferences.ad_storage,
    ad_user_data: preferences.ad_user_data,
    ad_personalization: preferences.ad_personalization,
    functionality_storage: preferences.functionality_storage,
    personalization_storage: preferences.personalization_storage,
    security_storage: preferences.security_storage,
    trigger: 'user_interaction',
  });

  // Lets in-memory analytics state (visitor id, attribution) persist once allowed.
  try { window.dispatchEvent(new Event(CONSENT_EVENT)); } catch { /* ignore */ }
};

// Convert user preferences to consent format. Google's personalization_storage
// (content recommendations) goes with marketing: nothing on the site uses it.
const preferencesToConsent = (prefs: UserPreferences): ConsentPreferences => ({
  ad_storage: prefs.marketing ? 'granted' : 'denied',
  ad_user_data: prefs.marketing ? 'granted' : 'denied',
  ad_personalization: prefs.marketing ? 'granted' : 'denied',
  analytics_storage: prefs.analytics ? 'granted' : 'denied',
  functionality_storage: 'granted',
  personalization_storage: prefs.marketing ? 'granted' : 'denied',
  security_storage: 'granted',
});

// Convert consent to user preferences
const consentToPreferences = (consent: ConsentPreferences): UserPreferences => ({
  necessary: true,
  analytics: consent.analytics_storage === 'granted',
  marketing: consent.ad_storage === 'granted',
});

/**
 * Microsoft Clarity's own consent call. Clarity loads only once analytics is
 * allowed (GTM consent check) and reads Google Consent Mode when it starts;
 * this tells a copy that is already running about a later change.
 */
const signalClarity = (prefs: UserPreferences) => {
  try {
    const clarity = (window as unknown as { clarity?: (...a: unknown[]) => void }).clarity;
    if (typeof clarity !== 'function') return;
    clarity('consentv2', {
      ad_Storage: prefs.marketing ? 'granted' : 'denied',
      analytics_Storage: prefs.analytics ? 'granted' : 'denied',
    });
  } catch { /* ignore */ }
};

/** Our own analytics counts how visitors answer the banner (no identifiers). */
const recordOwn = (event: string, params: Record<string, unknown> = {}) => {
  void import('@/lib/ownAnalytics').then((m) => m.sendOwnEvent(event, params)).catch(() => {});
};

/** Categories the visitor has not allowed. */
const refused = (prefs: UserPreferences): ConsentCategory[] => [
  ...(prefs.analytics ? [] : (['analytics'] as const)),
  ...(prefs.marketing ? [] : (['marketing'] as const)),
];

export const useCookieConsent = () => {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null);
  // Outside Europe analytics is on until the visitor turns it off (see the
  // regional consent defaults in index.html), so the toggles start that way.
  const outsideEurope = typeof window !== 'undefined' && (window as unknown as { __ecConsentRegion?: string }).__ecConsentRegion === 'other';
  const [preferences, setPreferences] = useState<UserPreferences>({
    necessary: true,
    analytics: outsideEurope,
    marketing: false,
  });
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Initialize on mount - just load saved state, don't push events
  // TrackingManager handles initial consent events
  useEffect(() => {
    ensureDataLayer();

    const showBannerNow = () => {
      setHasConsented(false);
      setShowBanner(true);
      recordOwn('consent_banner_shown');
    };

    try {
      const savedConsent = safeGetItem(CONSENT_STORAGE_KEY);
      const savedVersion = safeGetItem(CONSENT_VERSION_KEY);
      const savedTimestamp = safeGetItem(CONSENT_TIMESTAMP_KEY);

      // No prior consent → show banner
      if (!savedConsent) {
        showBannerNow();
        return;
      }

      // Outdated schema version → re-ask
      if (savedVersion !== CURRENT_CONSENT_VERSION) {
        showBannerNow();
        return;
      }

      // Expired consent (>6 months) → re-ask
      if (savedTimestamp) {
        const age = Date.now() - new Date(savedTimestamp).getTime();
        if (Number.isFinite(age) && age > CONSENT_EXPIRY_MS) {
          showBannerNow();
          return;
        }
      }

      const parsed = JSON.parse(savedConsent);
      if (!isValidConsent(parsed)) {
        showBannerNow();
        return;
      }

      const prefs = consentToPreferences(parsed);
      // Anything a refused category left behind (a script that ignored the
      // choice, an older visit) is removed on every page load.
      purgeCategories(refused(prefs));
      setPreferences(prefs);
      setHasConsented(true);
      setShowBanner(false);
    } catch (err) {
      // Any unexpected failure (blocked storage, JSON error, etc.) → show banner
      console.warn('[CookieConsent] Failed to read saved consent, showing banner', err);
      showBannerNow();
    }
  }, []);

  const saveConsent = useCallback((userPrefs: UserPreferences) => {
    const consent = preferencesToConsent(userPrefs);

    // Save to localStorage (gracefully handle blocked storage)
    safeSetItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    safeSetItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
    safeSetItem(CONSENT_VERSION_KEY, CURRENT_CONSENT_VERSION);

    // Update Google Consent Mode, then Clarity if it is already running.
    updateConsent(consent);
    signalClarity(userPrefs);

    // A refused or withdrawn category leaves nothing behind on the device.
    purgeCategories(refused(userPrefs));

    recordOwn('consent_choice', {
      choice: userPrefs.analytics && userPrefs.marketing ? 'accept_all'
        : !userPrefs.analytics && !userPrefs.marketing ? 'reject_all' : 'custom',
      analytics: userPrefs.analytics,
      marketing: userPrefs.marketing,
    });

    // Update state
    setPreferences(userPrefs);
    setHasConsented(true);
    setShowBanner(false);
    setShowModal(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  }, [saveConsent]);

  const savePreferences = useCallback((prefs: UserPreferences) => {
    saveConsent({ ...prefs, necessary: true });
  }, [saveConsent]);

  const openPreferences = useCallback(() => {
    setShowModal(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowModal(false);
  }, []);

  const reopenBanner = useCallback(() => {
    setShowModal(true);
  }, []);

  return {
    hasConsented,
    preferences,
    showBanner,
    showModal,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    reopenBanner,
    setPreferences,
  };
};

// Declare gtag on window
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
